import { useEffect, useRef, useState } from 'react'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { sileo } from 'sileo'
import CustomButton from '../../../../../ui/CustomButton'

export const useLinkageDocuments = ({ open, action, idLibro, idOrigin, businessId, businessAPI, enabled = true }) => {
  const [files, setFiles] = useState([])
  const [documents, setDocuments] = useState([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState(null)
  const uploadedFiles = useRef(new Set())
  const uploadRequest = useRef(null)
  const inputRef = useRef(null)
  const editing = action === 'modificar'

  useEffect(() => {
    setFiles([])
    setDocuments([])
    setError('')
    uploadedFiles.current = new Set()
    uploadRequest.current = null
  }, [open, action, idLibro, idOrigin, businessId])

  useEffect(() => {
    if (!open || !enabled || !editing || !businessId) return
    let active = true
    setDocuments([])
    if (idOrigin == null || String(idOrigin).trim() === '') {
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    const load = async () => {
      try {
        const { data } = await businessAPI.getBusinessDocuments(businessId)
        const rows = Array.isArray(data) ? data : data?.documents ?? data?.data?.documents ?? data?.data
        if (!Array.isArray(rows)) throw new Error('Formato de documentos desconocido')
        if (active) setDocuments(rows.filter(document =>
          document.comprobanteManualId != null && String(document.comprobanteManualId) === String(idOrigin)
        ))
      } catch (error) {
        if (active) setError('No se pudieron cargar los documentos de la empresa.')
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => { active = false }
  }, [open, enabled, editing, businessId, idLibro, idOrigin, businessAPI])

  // Se llama después de crear la vinculación: sube un archivo por llamada, en orden.
  // Un fallo no revierte la vinculación ni corta los demás; al final avisa cuáles fallaron.
  const upload = async comprobanteManualId => {
    const pending = files.filter(item => !uploadedFiles.current.has(item))
    if (!enabled || editing || pending.length === 0) return
    if (uploadRequest.current) return uploadRequest.current
    setError('')
    const request = async () => {
      const failed = []
      try {
        for (const selectedFile of pending) {
          try {
            if (comprobanteManualId == null) throw new Error('La creación no devolvió el ID de la vinculación')
            const payload = new FormData()
            payload.append('file', selectedFile)
            payload.append('comprobanteManualId', String(comprobanteManualId))
            await businessAPI.uploadBusinessDocument(businessId, payload)
            uploadedFiles.current.add(selectedFile)
          } catch (error) {
            console.error(`Error al subir ${selectedFile.name} a la vinculación:`, error.message)
            failed.push(selectedFile.name)
          }
        }
        if (failed.length > 0) {
          sileo.warning({
            title: 'Documentos no subidos',
            description: `La vinculación se creó, pero no se pudieron subir: ${failed.join(', ')}.`
          })
        }
      } finally {
        uploadRequest.current = null
      }
    }
    uploadRequest.current = request()
    return uploadRequest.current
  }

  const view = document => {
    try {
      const url = new URL(document.url ?? document.downloadUrl ?? document.download_url)
      if (!['https:', 'http:'].includes(url.protocol)) throw new Error('URL inválida')
      window.open(url.href, '_blank', 'noopener,noreferrer')
    } catch (error) {
      setError('La API no entregó un enlace válido para ver este documento.')
    }
  }

  const remove = async document => {
    if (document.id == null) {
      setError('La API no entregó el ID del documento para eliminarlo.')
      return
    }
    setBusyId(document.id)
    setError('')
    try {
      await businessAPI.deleteBusinessDocument(businessId, document.id)
      setDocuments(previous => previous.filter(item => item.id !== document.id))
    } catch (error) {
      setError('No se pudo eliminar el documento. Intenta nuevamente.')
    } finally {
      setBusyId(null)
    }
  }

  const panel = !enabled ? null : (
    <div className='w-full min-w-0 self-start rounded-2xl border border-gray-300 bg-gray-100 p-5 font-sans'>
      {editing ? <>
        <p className='text-center text-sm font-semibold text-gray-800'>Documentos de la empresa</p>
        <div className='my-4 max-h-48 overflow-y-auto rounded-2xl bg-white px-4 py-3'>
        {loading ? <p role='status'>Cargando documentos...</p> : documents.length === 0 && !error ?
          <p className='mt-2 text-sm text-gray-500'>No hay documentos asociados a esta vinculación.</p> : null}
        {documents.map(document => (
          <div key={document.id} className='flex min-w-0 items-center justify-between gap-3 py-2'>
            <span className='min-w-0 flex-1 truncate text-sm' title={document.name ?? document.filename ?? document.fileName}>{document.name ?? document.filename ?? document.fileName ?? 'Documento adjunto'}</span>
            <div className='flex shrink-0 items-center gap-3'>
              <button type='button' title='Ver documento' aria-label='Ver documento' onClick={() => view(document)}
                disabled={busyId !== null} className='flex items-center gap-1 text-sm text-accent2'>
                <VisibilityOutlinedIcon fontSize='small' /> Ver documento
              </button>
              <button type='button' title='Eliminar documento' aria-label='Eliminar documento' onClick={() => remove(document)}
                disabled={busyId !== null} className='text-red-500 disabled:opacity-50'>
                <DeleteOutlineIcon fontSize='small' />
              </button>
            </div>
          </div>
        ))}
        </div>
      </> : <>
        <p className='text-center text-sm font-semibold text-gray-800'>Archivos adjuntos (opcional)</p>
        <p className='mt-1 text-center text-xs text-gray-500'>Se aceptan archivos PDF, Word, Excel o CSV de hasta 50 MB.</p>
        <div className='my-4 max-h-48 overflow-y-auto rounded-2xl bg-white p-5 text-center' aria-live='polite'>
          {files.length === 0 ? <p className='text-sm text-gray-500'>Aún no has seleccionado archivos.</p> :
            files.map((item, index) => (
              <div key={index} className='flex items-center justify-between gap-3 py-1'>
                <span className='break-all text-left text-sm text-gray-500'>{item.name}</span>
                <button type='button' aria-label={`Quitar ${item.name}`} className='shrink-0 text-xs text-red-500 hover:text-red-700'
                  onClick={() => { setFiles(previous => previous.filter((_, i) => i !== index)); setError('') }}>Quitar</button>
              </div>
            ))}
        </div>
        <input ref={inputRef} type='file' multiple accept='.pdf,.doc,.docx,.xls,.xlsx,.csv' className='hidden'
          aria-label='Archivo adjunto opcional' onChange={event => {
            const selected = Array.from(event.target.files ?? [])
            event.target.value = ''
            if (selected.length === 0) return
            const rejected = []
            const accepted = selected.filter(item => {
              if (!/\.(pdf|doc|docx|xls|xlsx|csv)$/i.test(item.name)) rejected.push(`${item.name} (tipo no permitido)`)
              else if (item.size > 50 * 1024 * 1024) rejected.push(`${item.name} (supera 50 MB)`)
              else return true
              return false
            })
            setFiles(previous => [...previous, ...accepted])
            setError(rejected.length > 0 ? `No se agregaron: ${rejected.join(', ')}.` : '')
          }} />
        <CustomButton type='button' color='primary' className='w-full rounded-full'
          title={files.length > 0 ? 'AGREGAR ARCHIVOS' : 'SELECCIONAR ARCHIVOS'} onClick={() => inputRef.current?.click()} />
      </>}
      {error && <p role='alert' className='mt-3 text-sm text-red-500'>{error}</p>}
    </div>
  )

  return { upload, panel, busy: loading || busyId !== null }
}
